import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './DraftPage.module.css';
import DraftFilterModal from '../../components/modal/DraftFilterModal';
import DraftInfoModal from '../../components/modal/DraftInfoModal';
import DraftBundleInfoModal from '../../components/modal/DraftBundleInfoModal';

import filterIcon from '../../img/common/filter.png';
import draftListIcon from '../../img/common/draftListIcon.png';
import upArrowIcon from '../../img/common/UpTriangleBlack.png';
import downArrowIcon from '../../img/common/DownTriangleBlack.png';

const ITEMS_PER_PAGE = 20;
const PAGE_BLOCK_SIZE = 10;
const STATUS_LABELS = { pending: '대기', rejected: '반려', approved: '승인', canceled: '취소' };

const COLUMNS = [
    { labelKey: 'Draft Registration Date', filterType: 'date',     sortKey: null },
    { labelKey: 'GBCC Key',                filterType: null,       sortKey: 'draftDocumentId' },
    { labelKey: 'Team Name',               filterType: 'team',     sortKey: null },
    { labelKey: 'Reporter',                filterType: null,       sortKey: 'draftUserName' },
    { labelKey: 'Draft Target',            filterType: 'target',   sortKey: null },
    { labelKey: 'Category',                filterType: 'category', sortKey: null },
    { labelKey: 'Approval Status',         filterType: 'status',   sortKey: null },
];

const postJson = (url) => fetch(url, { method: 'post', headers: { 'content-type': 'application/json' } }).then(r => r.json());

const formatDate = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function DraftPage() {
    const { t } = useTranslation();
    const { draftDocumentId } = useParams();

    const [allDraftList, setAllDraftList] = useState([]);
    const [userName, setUserName] = useState('');
    const [userInfo, setUserInfo] = useState({});
    const [allOrganoidKeys, setAllOrganoidKeys] = useState([]);
    const [reDraw, setReDraw] = useState(0);
    const [selectedTab, setSelectedTab] = useState('default');
    const [onlyMine, setOnlyMine] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

    const [dateFilter, setDateFilter] = useState({ startDate: '', endDate: '' });
    const [teamFilter, setTeamFilter] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState([]);
    const [targetFilter, setTargetFilter] = useState('');
    const [tempTargetKeyword, setTempTargetKeyword] = useState('');

    const [filterModal, setFilterModal] = useState({ show: false, type: '' });
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showBundleInfoModal, setShowBundleInfoModal] = useState(false);
    const [showTargetFilterModal, setShowTargetFilterModal] = useState(false);
    const [selectedDraft, setSelectedDraft] = useState({});

    useEffect(() => { postJson('/api/getOrganoidInfoKeyList').then(setAllOrganoidKeys); }, []);
    useEffect(() => { postJson('/api/getAllDraftList').then(setAllDraftList); }, [reDraw]);
    useEffect(() => {
        postJson('/api/getLoginedUserInfo').then(data => {
            setUserName(data.data[0]?.userName);
            setUserInfo(data?.data[0]);
        });
    }, []);

    const organoidKeyMap = useMemo(() => {
        const m = new Map();
        allOrganoidKeys.forEach(item => m.set(item.time, item.Organoid_Info_Key));
        return m;
    }, [allOrganoidKeys]);

    const getDisplayTarget = useCallback((targetTime) => {
        if (typeof targetTime !== 'string') return '';
        return (organoidKeyMap.get(targetTime) ?? targetTime).replace(/hashmark/g, '#');
    }, [organoidKeyMap]);

    const formatStat = useCallback((s) => STATUS_LABELS[s] ? t(STATUS_LABELS[s]) : '', [t]);

    const filteredAndSortedDrafts = useMemo(() => {
        const filtered = allDraftList.filter(draft => {
            if (dateFilter.startDate) {
                const start = new Date(dateFilter.startDate); start.setHours(0, 0, 0, 0);
                if (new Date(draft.regTime) < start) return false;
            }
            if (dateFilter.endDate) {
                const end = new Date(dateFilter.endDate); end.setHours(23, 59, 59, 999);
                if (new Date(draft.regTime) > end) return false;
            }
            if (teamFilter.length && !teamFilter.includes(draft.draftUserTeamName)) return false;
            if (categoryFilter.length && !categoryFilter.includes(draft.draftCategory)) return false;
            if (statusFilter.length && !statusFilter.includes(draft.authorizedStatus)) return false;

            if (targetFilter) {
                const kw = targetFilter.toLowerCase();
                const variants = [kw, kw.replace(/#/g, 'hashmark'), kw.replace(/hashmark/g, '#')];
                const raw = String(draft.targetKey || '').toLowerCase();
                const disp = getDisplayTarget(draft.targetKey).toLowerCase();
                if (!variants.some(v => raw.includes(v) || disp.includes(v))) return false;
            }
            if (onlyMine && draft.draftUserName !== userName && draft.authorizedPersonName !== userName) return false;
            return true;
        });

        if (!sortConfig.key || !sortConfig.direction) return filtered;
        const dir = sortConfig.direction === 'ascending' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key]) return -dir;
            if (a[sortConfig.key] > b[sortConfig.key]) return dir;
            return 0;
        });
    }, [allDraftList, dateFilter, teamFilter, categoryFilter, statusFilter, targetFilter, onlyMine, userName, sortConfig, getDisplayTarget]);

    const totalPages = Math.ceil(filteredAndSortedDrafts.length / ITEMS_PER_PAGE);
    const currentDrafts = filteredAndSortedDrafts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
    const blockStart = Math.floor((currentPage - 1) / PAGE_BLOCK_SIZE) * PAGE_BLOCK_SIZE + 1;
    const blockEnd = Math.min(blockStart + PAGE_BLOCK_SIZE - 1, totalPages);

    const activeFilters = useMemo(() => {
        const labels = [];
        if (dateFilter.startDate || dateFilter.endDate) labels.push(`Date: ${dateFilter.startDate || 'Any'} ~ ${dateFilter.endDate || 'Any'}`);
        if (teamFilter.length) labels.push(`Team: ${teamFilter.join(', ')}`);
        if (targetFilter) labels.push(`Target: "${targetFilter}"`);
        if (categoryFilter.length) labels.push(`Category: ${categoryFilter.join(', ')}`);
        if (statusFilter.length) labels.push(`Status: ${statusFilter.map(formatStat).join(', ')}`);
        return labels;
    }, [dateFilter, teamFilter, categoryFilter, statusFilter, targetFilter, formatStat]);

    useEffect(() => {
        if (!draftDocumentId || allDraftList.length === 0) return;
        const draft = allDraftList.find(d => String(d.draftDocumentId) === draftDocumentId);
        if (!draft) return;
        setSelectedDraft(draft);
        draft.bundleId ? setShowBundleInfoModal(true) : setShowInfoModal(true);
    }, [draftDocumentId, allDraftList]);

    const handleTabChange = (tab) => { setSelectedTab(tab); setOnlyMine(tab === 'myDraft'); setCurrentPage(1); };

    const applyFilter = (filter) => {
        const map = {
            date:     () => setDateFilter(filter),
            team:     () => setTeamFilter(filter.selectedTeams),
            category: () => setCategoryFilter(filter.selectedCategories),
            status:   () => setStatusFilter(filter.selectedStatuses),
        };
        map[filterModal.type]?.();
        setCurrentPage(1);
    };

    const openInfoModal = (draft) => {
        setSelectedDraft(draft);
        draft.bundleId ? setShowBundleInfoModal(true) : setShowInfoModal(true);
    };

    const requestSort = (key) => setSortConfig(prev => {
        if (prev.key !== key) return { key, direction: 'ascending' };
        if (prev.direction === 'ascending') return { key, direction: 'descending' };
        return { key: '', direction: '' };
    });

    const clearSort = () => {
        setDateFilter({ startDate: '', endDate: '' });
        setTeamFilter([]); setCategoryFilter([]); setStatusFilter([]); setTargetFilter('');
        setSortConfig({ key: '', direction: '' });
        setCurrentPage(1);
    };

    const applyTargetKeyword = () => {
        setTargetFilter(tempTargetKeyword);
        setCurrentPage(1);
        setShowTargetFilterModal(false);
    };

    return (
        <div className={styles.rightTab}>
            <div className={styles.topMenu}>
                <div className={styles.curOrganoid}>
                    <img src={draftListIcon} className={styles.draftListIcon} alt="icon" />
                    &nbsp;{t('기안 리스트')}
                </div>
                <div className={selectedTab === 'default' ? styles.topTabFront : styles.topTabBack} onClick={() => handleTabChange('default')}>{t('View All Drafts')}</div>
                <div />
                <div className={selectedTab === 'myDraft' ? styles.topTabFront : styles.topTabBack} onClick={() => handleTabChange('myDraft')}>{t('View My Drafts Only')}</div>
            </div>

            <div className={styles.rightTabBottomBox}>
                <div className={styles.filterInfoContainer}>
                    <button className={styles.clearFilterButton} onClick={clearSort}>{t('필터링 및 정렬 취소')}</button>
                    <div className={styles.countInfo}>
                        <span>Total: <strong>{allDraftList.length}</strong></span>
                        {activeFilters.length > 0 && <span style={{ color: '#007bff', marginLeft: '10px' }}>Filtered: <strong>{filteredAndSortedDrafts.length}</strong></span>}
                    </div>
                    {activeFilters.length > 0 && (
                        <div className={styles.activeFiltersList}>
                            {activeFilters.map(label => <span key={label} className={styles.filterBadge}>{label}</span>)}
                        </div>
                    )}
                </div>

                <div className={styles.draftColumnTitle}>
                    {COLUMNS.map(col => (
                        <div key={col.labelKey} className={styles.columnTitle}
                            onClick={() => col.sortKey && requestSort(col.sortKey)}
                            style={col.sortKey ? { cursor: 'pointer' } : undefined}>
                            {t(col.labelKey)}
                            {col.filterType && (
                                <img src={filterIcon} className={styles.filterIcon} alt="filter"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (col.filterType === 'target') { setTempTargetKeyword(targetFilter); setShowTargetFilterModal(true); }
                                        else setFilterModal({ show: true, type: col.filterType });
                                    }} />
                            )}
                            {col.sortKey && sortConfig.key === col.sortKey && sortConfig.direction && (
                                <img src={sortConfig.direction === 'ascending' ? upArrowIcon : downArrowIcon} className={styles.sortIcon} alt="sort" />
                            )}
                        </div>
                    ))}
                </div>

                <div className={styles.draftsList}>
                    {currentDrafts.map(draft => (
                        <div key={draft.draftDocumentId} className={styles.draftInfo} onClick={() => openInfoModal(draft)}>
                            <div className={styles.draftDetailValue}>{formatDate(draft.regTime)}</div>
                            <div className={styles.draftDetailValue}>{draft.draftDocumentId}</div>
                            <div className={styles.draftDetailValue}>{draft.draftUserTeamName}</div>
                            <div className={styles.draftDetailValue}>{draft.draftUserName}</div>
                            <div className={styles.draftDetailValue}>{getDisplayTarget(draft.targetKey)}</div>
                            <div className={styles.draftDetailValue}>{t(draft.draftCategory)}</div>
                            <div className={styles.draftDetailValue}>{formatStat(draft.authorizedStatus)}</div>
                        </div>
                    ))}
                </div>

                {totalPages > 0 && (
                    <div className={styles.pagination}>
                        {blockStart > 1 && (<>
                            <div onClick={() => setCurrentPage(1)} className={styles.sideButton}>&laquo;</div>
                            <div onClick={() => setCurrentPage(blockStart - 1)} className={styles.sideButton}>&lt;</div>
                        </>)}
                        {Array.from({ length: blockEnd - blockStart + 1 }, (_, i) => blockStart + i).map(p => (
                            <div key={p} className={`${styles.pageButton} ${p === currentPage ? styles.active : ''}`} onClick={() => setCurrentPage(p)}>{p}</div>
                        ))}
                        {blockEnd < totalPages && (<>
                            <div onClick={() => setCurrentPage(blockEnd + 1)} className={styles.sideButton}>&gt;</div>
                            <div onClick={() => setCurrentPage(totalPages)} className={styles.sideButton}>&raquo;</div>
                        </>)}
                    </div>
                )}
            </div>

            {showTargetFilterModal && (
                <div className={styles.targetModalOverlay}>
                    <div className={styles.targetModalBox}>
                        <h3 className={styles.targetModalTitle}>{t('Draft Target 검색')}</h3>
                        <input type="text" value={tempTargetKeyword} autoFocus
                            onChange={(e) => setTempTargetKeyword(e.target.value)}
                            placeholder={t('검색어를 입력하세요')}
                            onKeyDown={(e) => e.key === 'Enter' && applyTargetKeyword()}
                            className={styles.targetModalInput} />
                        <div className={styles.targetModalActions}>
                            <button onClick={() => setShowTargetFilterModal(false)} className={styles.targetModalCancel}>{t('취소')}</button>
                            <button onClick={applyTargetKeyword} className={styles.targetModalApply}>{t('적용')}</button>
                        </div>
                    </div>
                </div>
            )}

            <DraftFilterModal show={filterModal.show} onClose={() => setFilterModal({ show: false, type: '' })} onApply={applyFilter} filterType={filterModal.type} />
            <DraftInfoModal show={showInfoModal} onClose={() => setShowInfoModal(false)} selectedDraft={selectedDraft} reDrawView={() => setReDraw(p => p + 1)} />
            <DraftBundleInfoModal show={showBundleInfoModal} onClose={() => setShowBundleInfoModal(false)} selectedDraft={selectedDraft} reDrawView={() => setReDraw(p => p + 1)} />
        </div>
    );
}

export default DraftPage;